/**
 * Why it ranks, in points (D147 of documentation/pr-plans/insights-simplify-plan.md, P43; board 7b), in plain
 * words since D153, as sketch 5b since D171 and as sketch 7 since D192. Pure: a row's boosts in, the panel out, so
 * the activity table, Search ideas and Your top 5 draw one panel, and a test runs it under Node.
 *
 * The score is the sum of the boosts the row carries (OpportunityPoints). The panel leads with the aim, then five
 * checks in a fixed order, each named for what it checks with the value it found under it, its earned part a band
 * as thick as its points running into one 0 to 100 bar, where its points are written; hover or focus reads a
 * check's result in plain words under the drawing, which says nothing at rest (D192), and a press opens its steps
 * with this search's step marked; after them only the guide link, small and faint (Show what would raise it went on
 * Ola's 22 Oct review of P77). Only Value changes in the browser: a row moved into or out of Bottom of funnel in place (writes.js placed()) takes its
 * buyerPoints there and 0 elsewhere (D130), as the server would give it, so the panel and the list's order never
 * disagree.
 */

const VALUE = "value";

/** The most a score can be (OpportunityPoints::MAX): the bar every row's bands run into. */
export const MAX_POINTS = 100;

/** The keys of the boosts, in the order the panel draws them (OpportunityPoints::KEYS). */
export const POINT_KEYS = Object.freeze(["potential_clicks", "position", "keyword_difficulty", VALUE, "visitor_quality"]);

/**
 * `words` with every `:key` replaced through a function, so a `$` in a value is never read as a pattern; every
 * occurrence, as Laravel's trans() fills them.
 */
export function fill(words, values) {
    return Object.keys(values).reduce((text, key) => text.replaceAll(`:${key}`, () => String(values[key])), String(words ?? ""));
}

/** The boosts among a row's factors: Search intent is the order, not a boost, and has none. */
export function boostsOf(factors) {
    return (Array.isArray(factors) ? factors : []).filter((factor) => POINT_KEYS.includes(factor?.key));
}

/** Value's boost at a search intent (OpportunityPoints::value()): the factor's buyerPoints at Bottom of funnel, 0 elsewhere. */
export function valuePoints(factor, stage) {
    return (stage ?? null) === "bottom" ? Number(factor?.buyerPoints ?? factor?.points ?? 0) : 0;
}

/** The score: the sum of the boosts a row carries, as the server summed them. */
export function pointsOf(factors) {
    return boostsOf(factors).reduce((sum, factor) => sum + (Number(factor.points) || 0), 0);
}

// The picture's frame, in px: the label column, where each check's button sits; the nodes, one per check, as tall
// as the most it can add; the score bar, with each check's points written beside its part; and the bar's
// label. Two px a point (scale), so a check's part of the bar is a line tall from a few points up.
const FRAME = Object.freeze({ width: 492, label: 204, node: 216, nodeWidth: 6, bar: 318, barWidth: 14, points: 338, labels: 404, labelsWidth: 88, scale: 2, gap: 8, buttonHeight: 30 });

// A bar label's height (its figure over one line), and a points label's (one line).
const BAR_LABEL_PX = 40;
const POINTS_PX = 13;

function ribbon(x0, top0, bottom0, x1, top1, bottom1) {
    const mid = (x0 + x1) / 2;
    const at = (value) => value.toFixed(2);

    return `M${x0},${at(top0)}C${mid},${at(top0)} ${mid},${at(top1)} ${x1},${at(top1)}L${x1},${at(bottom1)}C${mid},${at(bottom1)} ${mid},${at(bottom0)} ${x0},${at(bottom0)}Z`;
}

function rect(x, y, width, height) {
    return `M${x.toFixed(2)},${y.toFixed(2)}h${width.toFixed(2)}v${height.toFixed(2)}h${(-width).toFixed(2)}Z`;
}

// Labels held inside the picture and apart from each other: each at least `size` under the one above, none past
// the bottom, so two small parts of the bar never write over each other.
function spaced(labels, size, height) {
    labels.forEach((label, index) => {
        label.top = Math.max(index === 0 ? 0 : labels[index - 1].top + size, Math.round(label.top));
    });

    for (let index = labels.length - 1; index >= 0; index -= 1) {
        labels[index].top = Math.max(0, Math.min(labels[index].top, index === labels.length - 1 ? height - size : labels[index + 1].top - size));
    }

    return labels;
}

/**
 * The picture: every check's node on the left, in the panel's order, as tall as the most it can add, its earned
 * part solid under its missing part faint; the earned part running as a band into the score bar on the right, where
 * the points earned stack under the rest not earned, so the bar reads as a 0 to 100 axis; and each check's points
 * beside its part of the bar. Each key's paths are grouped so the template binds a fixed set of <path>s: a
 * <template> inside an <svg> is no HTML template, so Alpine cannot repeat one there.
 */
export function picture(checks, score, words) {
    const f = FRAME;
    const k = f.scale;
    const barHeight = MAX_POINTS * k;
    const rows = [];
    let y = 0;

    checks.forEach((check, index) => {
        const height = check.max * k;
        const slot = Math.max(height, f.buttonHeight);
        rows.push({ key: check.key, pointsText: check.pointsText, height, earned: check.points * k, missing: Math.max(0, check.max - check.points) * k, y: y + (slot - height) / 2, top: Math.round(y + (slot - f.buttonHeight) / 2) });
        y += slot + (index < checks.length - 1 ? f.gap : 0);
    });

    const height = Math.ceil(Math.max(y, barHeight));
    const barTop = (height - barHeight) / 2;
    const missing = Math.max(0, MAX_POINTS - score);
    const earnedTop = barTop + missing * k;
    const x0 = f.node + f.nodeWidth;
    const paths = { bands: {}, nodes: {}, rests: {}, score: "", track: "" };
    const points = [];
    const tops = {};
    let landAt = earnedTop;

    rows.forEach((row) => {
        tops[row.key] = row.top;

        if (row.missing > 0) {
            paths.rests[row.key] = rect(f.node, row.y, f.nodeWidth, row.missing);
        }

        if (row.earned > 0) {
            paths.nodes[row.key] = rect(f.node, row.y + row.missing, f.nodeWidth, row.earned);
            paths.bands[row.key] = ribbon(x0, row.y + row.missing, row.y + row.height, f.bar, landAt, landAt + row.earned);
            points.push({ key: row.key, text: row.pointsText, top: landAt + row.earned / 2 - POINTS_PX / 2 });
            landAt += row.earned;
        }
    });

    paths.track = missing > 0 ? rect(f.bar, barTop, f.barWidth, missing * k) : "";
    paths.score = score > 0 ? rect(f.bar, earnedTop, f.barWidth, Math.min(score, MAX_POINTS) * k) : "";

    // An empty d makes the browser log a parse error: a part with nothing to draw draws nothing and says nothing.
    POINT_KEYS.forEach((key) => {
        ["bands", "nodes", "rests"].forEach((part) => {
            paths[part][key] = paths[part][key] || "M0,0";
        });
    });
    ["score", "track"].forEach((key) => {
        if (paths[key] === "") paths[key] = "M0,0";
    });

    // The rest not earned beside the faint part. The points earned are not labelled again: they are the score at
    // the panel's top (P84, Ola, 23 Oct).
    const labels = [];

    if (missing > 0) {
        labels.push({ kind: "still", big: String(missing), line: words.not_earned ?? "", top: barTop + (missing * k) / 2 - BAR_LABEL_PX / 2 });
    }

    return { height, labelWidth: f.label, tops, paths, points: spaced(points, POINTS_PX, height), labels: spaced(labels, BAR_LABEL_PX, height) };
}

/**
 * One check (D153, D171): its name and the value it found, its points of the most it can add, its plain words for
 * hover and focus, and its steps for a press (the scale the server read from config, this search's step marked,
 * the line between them coloured up to the mark, read aloud as one sentence).
 */
function check(factor, words) {
    const key = factor.key;
    const name = words.names?.[key] ?? factor.label ?? key;
    const points = Number(factor.points) || 0;
    const max = Number(factor.max) || 0;
    const short = String(factor.short ?? factor.fact ?? "");
    const steps = Array.isArray(words.steps?.[key]) ? words.steps[key] : [];
    const n = steps.length;
    const marked = Number.isInteger(factor.step) && factor.step >= 0 && factor.step < n ? factor.step : null;
    const percent = (value) => `${(Math.round(value * 1000) / 1000).toString()}%`;
    const line = n > 1 ? { left: percent(50 / n), width: percent(((n - 1) * 100) / n), filled: percent(marked === null ? 0 : (marked * 100) / n) } : { left: "0%", width: "0%", filled: "0%" };
    const label = (step) => (step.sub ? `${step.label} ${step.sub}` : step.label);
    const spokenSteps = steps.map((step) => fill(words.spoken?.step ?? ":label, :points", { label: label(step), points: step.points })).join("; ");
    const spokenMark = marked === null ? fill(words.spoken?.none_marked ?? "", { points }) : fill(words.spoken?.marked ?? "", { label: label(steps[marked]), points: steps[marked].points });

    return {
        key,
        name,
        short,
        points,
        max,
        pointsText: fill(words.points ?? ":points of :max", { points, max }),
        pointsSpoken: fill(words.points_spoken ?? ":points of :max points", { points, max }),
        plain: String(factor.plain ?? factor.fact ?? ""),
        text: fill(words.check ?? ":name, up to :max points. :text", { name, max, text: fill(words.checks?.[key] ?? "", { max }) }),
        columns: `repeat(${Math.max(n, 1)}, minmax(0, 1fr))`,
        line,
        steps: steps.map((step, index) => ({ label: String(step.label), sub: step.sub ?? null, points: String(step.points), isMarked: index === marked, isReached: marked !== null && index <= marked })),
        spoken: `${fill(words.spoken?.steps ?? "", { steps: spokenSteps })} ${spokenMark}`.trim(),
    };
}

/**
 * The panel: the score out of 100, the aim as its headline with the figures behind it, and the five checks in the
 * panel's order with the picture of them running into the score bar. Nothing for under the drawing at rest (D192):
 * the lit check's own plain words are all that goes there, and the guide link is the template's own.
 *
 * @param {{factors: object[], stage?: ?string}} input
 * @param {object} words site-analytics.why_in_points (WhyInPoints::words())
 */
export function whyView(input, words) {
    const boosts = boostsOf(input.factors);
    const byKey = Object.fromEntries(boosts.map((factor) => [factor.key, factor]));
    // The five checks in the panel's order (D171), whatever order the row sent them in.
    const checks = POINT_KEYS.filter((key) => byKey[key]).map((key) => check(byKey[key], words));
    const score = pointsOf(boosts);
    const aim = byKey.potential_clicks?.aim;

    return {
        title: words.title,
        score: String(score),
        outOf: fill(words.out_of ?? "", { max: MAX_POINTS }),
        headline: aim && typeof aim === "object" ? String(aim.title ?? "") : "",
        headlineBody: aim && typeof aim === "object" && aim.body ? String(aim.body) : "",
        checks,
        picture: picture(checks, score, words),
    };
}

/**
 * The Alpine component for the lists the server draws (Search ideas, Your top 5): why_view(input) draws one input the
 * server sent with the words it was mounted with, as the activity table's why.js draws a row.
 *
 * @param {object} words site-analytics.why_in_points (WhyInPoints::words())
 */
export default function InsightsWhyItRanks(words) {
    const views = new WeakMap();

    return {
        why_view(input) {
            const kept = views.get(input);

            if (kept) {
                return kept;
            }

            const view = whyView(input, words);
            views.set(input, view);

            return view;
        },
    };
}
